import { useState, useEffect } from "react";
import usePWAInstall from "@/hooks/usePWAInstall";
import { Download, Smartphone, Share, Plus, X, Check, Sparkles } from "lucide-react";

const DISMISS_KEY = "talabya_install_banner_dismissed_v1";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Global sticky top banner prompting users to install the PWA.
 * - Appears immediately at site entry for ALL roles (anonymous + logged in).
 * - Hidden when app is already installed, unavailable, or user dismissed it in the last 24h.
 * - Uses native `beforeinstallprompt` on Android/Desktop, iOS instructions modal on iOS Safari.
 */
export default function InstallAppBanner() {
  const { available, installed, platform, install, hasNativePrompt } = usePWAInstall();
  const [showIosModal, setShowIosModal] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      if (!Number.isFinite(ts)) return false;
      return Date.now() - ts < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });
  const [installing, setInstalling] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);

  useEffect(() => {
    if (installed) setJustInstalled(true);
  }, [installed]);

  if (installed || justInstalled) return null;
  if (!available) return null;
  if (dismissed) return null;

  const handleInstall = async () => {
    if (hasNativePrompt) {
      setInstalling(true);
      const r = await install();
      setInstalling(false);
      if (r.ok) setJustInstalled(true);
    } else if (platform === "ios") {
      setShowIosModal(true);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore quota / privacy errors
    }
    setDismissed(true);
  };

  return (
    <>
      <div
        className="sticky top-0 z-[60] w-full animate-fade-up"
        data-testid="install-app-banner"
        style={{
          background: "linear-gradient(135deg, #2547D0 0%, #1E3AB0 100%)",
          boxShadow: "0 4px 20px rgba(37,71,208,0.35)",
        }}
      >
        <div className="relative max-w-6xl mx-auto px-3 sm:px-6 py-2.5 flex items-center gap-3">
          {/* App icon */}
          <div className="shrink-0 relative">
            <div
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/25"
            >
              <img src="/icon-192.png" alt="طلبية" className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-cover" />
            </div>
            <span className="absolute -top-1 -left-1 w-3 h-3 rounded-full bg-[#E05A3D] ring-2 ring-[#2547D0] animate-pulse" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0 text-white">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-black text-sm sm:text-base leading-tight" style={{ fontFamily: "Cairo" }}>
                حمّل تطبيق طلبية على جهازك
              </span>
              <span
                className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 text-white"
              >
                <Sparkles size={10} /> أسرع وأسهل
              </span>
            </div>
            <div className="text-[11px] sm:text-xs text-white/85 mt-0.5 leading-tight line-clamp-1">
              تجربة أسرع، إشعارات فورية، ووصول بضغطة واحدة
            </div>
          </div>

          {/* Install button */}
          <button
            onClick={handleInstall}
            disabled={installing}
            className="shrink-0 px-3 sm:px-4 py-2 rounded-xl font-black text-xs sm:text-sm bg-white text-[#2547D0] flex items-center gap-1.5 transition-all active:scale-[0.97] disabled:opacity-60"
            data-testid="install-app-banner-btn"
          >
            <Download size={14} strokeWidth={2.5} />
            <span>{installing ? "جارٍ..." : "تثبيت"}</span>
          </button>

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white/85 hover:bg-white/10 transition-colors"
            aria-label="إغلاق"
            data-testid="install-app-banner-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* iOS instructions modal */}
      {showIosModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosModal(false)}
          data-testid="ios-install-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-[var(--border)]">
              <div className="flex items-center gap-2">
                <Smartphone className="text-primary-brand" />
                <h2 className="text-xl font-black" style={{ fontFamily: "Cairo" }}>تثبيت التطبيق</h2>
              </div>
              <button onClick={() => setShowIosModal(false)} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-[var(--text-secondary)] mb-4">اتبع الخطوات التالية لتثبيت طلبية على شاشتك الرئيسية:</p>
              <ol className="flex flex-col gap-3">
                {[
                  { n: 1, icon: <Share size={18} />, text: "اضغط زر المشاركة (Share) في متصفح Safari" },
                  { n: 2, icon: <Plus size={18} />, text: "اختر «إضافة إلى الشاشة الرئيسية»" },
                  { n: 3, icon: <Check size={18} />, text: "اضغط «إضافة» — سيظهر تطبيق طلبية على شاشتك" },
                ].map((s) => (
                  <li key={s.n} className="flex items-start gap-3 p-3 bg-[var(--surface-alt)] rounded-2xl">
                    <div className="w-8 h-8 rounded-full bg-primary-brand text-white flex items-center justify-center font-black shrink-0" style={{ fontFamily: "Cairo" }}>
                      {s.n}
                    </div>
                    <div className="flex-1">
                      <div className="text-primary-brand mb-1">{s.icon}</div>
                      <div className="text-sm font-bold">{s.text}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <button onClick={() => setShowIosModal(false)} className="w-full mt-4 py-3 rounded-xl bg-primary-brand text-white font-bold">
                فهمت
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
